import React, { useState } from 'react';
import { Container, Row, Col } from 'reactstrap';
import './ConsultingForm.css';

const DEFAULT_MEETING_URL = 'https://meetings.hubspot.com/viacheslav-seifer';

const getCookieValue = (cookieName) => {
    if (typeof document === 'undefined') {
        return '';
    }

    const escapedCookieName = cookieName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = document.cookie.match(new RegExp(`(?:^|; )${escapedCookieName}=([^;]*)`));

    return match ? decodeURIComponent(match[1]) : '';
};

const ConsultingForm = ({
    formId,
    title,
    subtitle,
    conversionSendTo,
    conversionValue = 50.0,
    requireHubspotCookie = false,
    meetingUrl = DEFAULT_MEETING_URL,
    language = 'en'
}) => {
    const isHebrew = language === 'he';
    const [submitted, setSubmitted] = useState(false);
    const [showMeetingFallback, setShowMeetingFallback] = useState(false);
    const [formData, setFormData] = useState({
        firstname: '',
        lastname: '',
        email: '',
        phone: '',
        message: ''
    });

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const hasHubspotCookie = () => Boolean(getCookieValue('hubspotutk'));

    const reportConversion = (url) => {
        if (typeof window !== 'undefined' && typeof window.gtag === 'function' && conversionSendTo) {
            var callback = function () {
                if (typeof url !== 'undefined') {
                    window.location = url;
                }
            };

            window.gtag('event', 'conversion', {
                send_to: conversionSendTo,
                value: conversionValue,
                currency: 'ILS',
                event_callback: callback
            });
        }

        return false;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (requireHubspotCookie && !hasHubspotCookie()) {
            setShowMeetingFallback(true);
            return;
        }

        reportConversion();
        setSubmitted(true);
    };

    return (
        <Container className="my-5">
            <Row className="justify-content-center">
                <Col lg={8} xl={7}>
                    {!submitted ? (
                        <div className="consulting-form-wrapper" dir={isHebrew ? 'rtl' : 'ltr'}>
                            <div className="form-header-epic">
                                <h3>{title || (isHebrew ? 'נשמח לשמוע מכם' : 'Let us hear from you')}</h3>
                                <p>{subtitle || (isHebrew
                                    ? 'השאירו כמה פרטים ונחזור אליכם עם מענה מקצועי והכוונה ראשונית.'
                                    : 'Leave a few details and we will get back to you with a professional response and initial guidance.')}</p>
                            </div>

                            {showMeetingFallback ? (
                                <div className="consulting-fallback-card" role="alert">
                                    <p className="consulting-fallback-message">
                                        נראה שהדפדפן שלך משתמש בהגדרות פרטיות מתקדמות, ולכן לא ניתן להשלים את שליחת הטופס בצורה מאובטחת דרך העמוד הזה.
                                        כדי שלא נפספס את הפנייה שלך, אפשר לקבוע איתנו שיחה קצרה ישירות ביומן.
                                    </p>
                                    <div className="consulting-fallback-actions">
                                        <a
                                            href={meetingUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="btn-submit-epic consulting-fallback-button"
                                        >
                                            קביעת שיחת ייעוץ
                                        </a>
                                        <p className="consulting-fallback-secondary-text">
                                            או לכתוב לנו בוואטסאפ או בטלפון
                                        </p>
                                        <div className="consulting-fallback-links">
                                            <a
                                                href="https://wa.me/message/V5BCTSNZ7OL4C1"
                                                target="_blank"
                                                rel="noreferrer"
                                                className="consulting-fallback-link"
                                            >
                                                וואטסאפ
                                            </a>
                                            <a
                                                href="tel:0515564767"
                                                className="consulting-fallback-link"
                                                dir="ltr"
                                            >
                                                051-5564767
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} id={formId}>
                                    <Row>
                                        <Col md={6} className="mb-4">
                                            <label className="label-epic">{isHebrew ? 'שם פרטי' : 'First name'}</label>
                                            <input
                                                type="text"
                                                name="firstname"
                                                className="input-epic"
                                                placeholder="ישראל"
                                                required
                                                value={formData.firstname}
                                                onChange={handleChange}
                                            />
                                        </Col>
                                        <Col md={6} className="mb-4">
                                            <label className="label-epic">{isHebrew ? 'שם משפחה' : 'Last name'}</label>
                                            <input
                                                type="text"
                                                name="lastname"
                                                className="input-epic"
                                                placeholder="ישראלי"
                                                required
                                                value={formData.lastname}
                                                onChange={handleChange}
                                            />
                                        </Col>
                                    </Row>

                                    <div className="mb-4">
                                        <label className="label-epic">{isHebrew ? 'אימייל עסקי' : 'Business email'}</label>
                                        <input
                                            type="email"
                                            name="email"
                                            className="input-epic"
                                            placeholder="name@company.com"
                                            dir="ltr"
                                            required
                                            value={formData.email}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="label-epic text-end">{isHebrew ? 'מספר טלפון' : 'Phone number'}</label>
                                        <div className="phone-input-wrapper">
                                            <input
                                                type="tel"
                                                name="phone"
                                                className="input-epic"
                                                placeholder="05X-XXXXXXX"
                                                required
                                                value={formData.phone}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <label className="label-epic">{isHebrew ? 'מה האתגר המרכזי?' : 'What is the main challenge?'}</label>
                                        <textarea
                                            name="message"
                                            className="input-epic"
                                            rows="4"
                                            placeholder="ספרו לנו בקצרה על המערכת, האתגר או הצורך העסקי שלכם..."
                                            required
                                            value={formData.message}
                                            onChange={handleChange}
                                        ></textarea>
                                    </div>

                                    <p className="form-disclaimer-epic">
                                        בשליחת הטופס אני מאשר/ת קבלת פניות במייל מ-epicfinal.
                                    </p>

                                    <button type="submit" className="btn-submit-epic">
                                        שלחו בקשה לייעוץ
                                    </button>
                                </form>
                            )}
                        </div>
                    ) : (
                        <div className="consulting-form-wrapper success-message-epic" dir={isHebrew ? 'rtl' : 'ltr'}>
                            <div className="success-icon-epic">
                                <i className="las la-check"></i>
                            </div>
                            <h3 style={{ color: '#fff', marginBottom: '16px' }}>הפנייה התקבלה</h3>
                            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '1.1rem' }}>
                                תודה שפניתם אלינו. נחזור אליכם בהקדם עם מענה ראשוני והמשך תיאום.
                            </p>
                            <button
                                className="btn-submit-epic"
                                style={{ maxWidth: '200px', margin: '30px auto 0' }}
                                onClick={() => setSubmitted(false)}
                            >
                                חזרה לטופס
                            </button>
                        </div>
                    )}
                </Col>
            </Row>
        </Container>
    );
};

export default ConsultingForm;
